import React, { createContext, useContext, useEffect, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import authConfig from '_/space-management/config/auth';
import { usersApi } from '_/space-management/services/api';
import { MembersModel } from '_/space-management/services/models/members.model';
import { AuthContextData, AuthState, LoginResponse } from '_/space-management/services/models/auth.model';
import { useLanguage } from '_/hooks/LanguageContext';

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

type AuthType = {
  children: React.ReactNode;
};

export const AuthProvider: React.FC<AuthType> = ({ children }) => {
  const [data, setData] = useState<AuthState>({} as AuthState);
  const [authLoading, setAuthLoading] = useState<boolean>(false);
  const { changeLanguage } = useLanguage();

  /**
   * Sign in using a MyInvenzi JWT token.
   * Authenticates with the SM server via POST /authentication (SmJwtStrategy),
   * which validates the MyInvenzi token and returns a SM session token + member.
   */
  const signInInvenzi = async (accessToken: string) => {
    try {
      const result = await usersApi.signInWithToken(accessToken);

      const { accessToken: smToken, member, refreshToken } = result;

      if (!member) throw new Error('No member found for this user in Space Management');

      usersApi.setToken(smToken, refreshToken);
      setData({ token: smToken, member, refreshToken });

      const toStore: [string, string][] = [
        [authConfig.tokenKey, smToken],
        [authConfig.memberKey, JSON.stringify(member)],
      ];
      if (refreshToken) toStore.push([authConfig.refreshTokenKey, refreshToken]);

      await AsyncStorage.multiSet(toStore);
    } catch (error) {
      console.log('Error signing in with token:', JSON.stringify(error, null, 2));
    }
    setAuthLoading(false);
  };

  const signIn = async ({ email, password }: { email: string; password: string }) => {
    try {
      const result = await usersApi.signIn({ email, password, strategy: 'local' });
      const { accessToken, member, refreshToken } = result;
      if (member) {
        await AsyncStorage.setItem(authConfig.memberKey, JSON.stringify(member));
      }
      setData({ token: accessToken, member, refreshToken });
      await AsyncStorage.multiSet([
        [authConfig.tokenKey, accessToken],
        [authConfig.refreshTokenKey, refreshToken],
      ]);

      return result;
    } catch (error) {
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await AsyncStorage.multiRemove([
        authConfig.tokenKey,
        authConfig?.memberKey,
        authConfig.refreshTokenKey,
      ]);
      setData({} as AuthState);
      usersApi.removeToken();
    } catch (error) {
      throw error;
    }
  };

  const updateMember = async (member: MembersModel) => {
    try {
      await AsyncStorage.setItem(authConfig.memberKey, JSON.stringify(member));
      setData((prevState) => ({
        ...prevState,
        member,
      }));
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    async function loadUser() {
      const [token, member, refreshToken] = await AsyncStorage.multiGet([
        authConfig.tokenKey,
        authConfig.memberKey,
        authConfig.refreshTokenKey,
      ]);

      if (token[1]) {
        usersApi.setToken(token[1], refreshToken[1] as string);
        setData({
          token: token[1],
          refreshToken: refreshToken[1] ?? undefined,
          member: member[1] ? JSON.parse(member[1]) : undefined,
        });
      }

      setAuthLoading(false);
    }

    setAuthLoading(true);
    loadUser();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        member: data.member,
        signIn,
        signOut,
        updateMember,
        signInInvenzi,
        authLoading,
        setAuthLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
